import React, { memo, useCallback, useEffect, useState } from 'react';
import {
  FlatList,
  Image,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
  View,
  Platform,
} from 'react-native';
import { Color, CommonProperties, CommonStyles } from '../../styles';
import BaseText from '../BaseText';
import { performExpenseAction } from '../../services/api/expense.service';
import { useNavigation } from '@react-navigation/native';
import { Modals } from '../../constants/ScreenNames';
import { isNullOrEmpty, isPdf } from '../../utils/common.utils';
import { scale } from '../../utils/scale.utils';
import ExpenseCheckbox from '../ExpenseCheckbox';
import { EXPENSE_MAPPED_INFO_FIELD_KEYS } from '../../constants/Enums';
import { formatDate } from '../../utils/expense.utils';
import moment from 'moment';
import { formatCurrency } from '../../utils/currency.utils';
import { useSelector } from 'react-redux';
import { selectCompanyCurrencyCulture } from '../../store/selectors/auth.selector';

const ReceiptItem = memo(
  ({ item, isSelected, onImagePress, onToggleSelect }) => {
    const receiptsVaultMappedFields = item?.[EXPENSE_MAPPED_INFO_FIELD_KEYS.RECEIPTS_VAULT];
    const { Merchant, Amount, Currency, ExpenseDate: ChargeDate } = receiptsVaultMappedFields || {};
    const formattedChargeDate = formatDate(ChargeDate?.value);
    const currencyCulture = useSelector(selectCompanyCurrencyCulture);
    const currency = !isNullOrEmpty(Currency?.value) ? Currency.value : 'USD';

    return (
      <View style={styles.itemContainer}>
        <TouchableOpacity style={styles.imageContainer} onPress={() => onImagePress(item)}>
          {isPdf(item.s3FileName) && Platform.OS !== 'ios' ? (
            <View style={styles.pdfIconContainer}>
              <BaseText style={styles.pdfIconText}>PDF</BaseText>
            </View>
          ) : (
            <Image style={styles.image} source={{ uri: item?.value }} />
          )}
        </TouchableOpacity>
        <TouchableOpacity style={styles.detailsContainer} onPress={() => onImagePress(item)}>
          <View>
            <BaseText style={styles.merchantText}>
              {!isNullOrEmpty(Merchant?.value) ? Merchant.value : 'Name not scanned'}
            </BaseText>
            <BaseText style={styles.dateText}>
              {!isNullOrEmpty(formattedChargeDate)
                ? moment(formattedChargeDate).format('MMM DD')
                : ' '}
            </BaseText>
          </View>
        </TouchableOpacity>

        <BaseText style={styles.amountText}>
          {!isNullOrEmpty(Amount?.value) && Amount.value !== 0
            ? formatCurrency(Amount.value, currency, currencyCulture, 2)
            : ''}
        </BaseText>
        <ExpenseCheckbox label="" checked={isSelected} onPress={() => onToggleSelect(item)} />
      </View>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.item.receiptId === nextProps.item.receiptId
    );
  }
);

const PhotoUploads = ({ toggleSelectImage, selectedImages }) => {
  const navigation = useNavigation();
  const [receipts, setReceipts] = useState([]);
  const [isFetching, setIsFetching] = useState(true);

  useEffect(() => {
    const fetchReceipts = async () => {
      const params = {
        ActionCode: 'getReceiptsOfUser',
      };
      const userReceipts = await performExpenseAction(params);
      const parsedReceipts = JSON.parse(userReceipts);
      if (isNullOrEmpty(parsedReceipts)) {
        setIsFetching(false);
        return;
      }
      parsedReceipts.sort((a, b) => new Date(b.createDate) - new Date(a.createDate));
      setReceipts(parsedReceipts);
      setIsFetching(false);
    };
    fetchReceipts();
  }, []);

  const renderItem = useCallback(
    ({ item }) => {
      const isSelected = selectedImages.some(({ receiptId }) => item.receiptId === receiptId);
      const removeSelectedImageFromState = (removedReceiptId) => {
        setReceipts([...receipts.filter((item) => item.receiptId !== removedReceiptId)]);
        if (selectedImages.some(({ receiptId }) => receiptId === removedReceiptId)) {
          toggleSelectImage({ receiptId: removedReceiptId });
        }
      };

      const handleImagePress = (receipt) => {
        navigation.navigate(Modals.ViewReceiptModal, {
          receipt,
          removeSelectedImageFromState,
          toggleSelectImage,
        });
      };

      return (
        <ReceiptItem
          item={item}
          isSelected={isSelected}
          onImagePress={handleImagePress}
          onToggleSelect={toggleSelectImage}
        />
      );
    },
    [selectedImages, toggleSelectImage, navigation, receipts]
  );

  return receipts && receipts.length > 0 ? (
    <FlatList
      data={receipts}
      renderItem={renderItem}
      keyExtractor={(_, index) => index.toString()}
      contentContainerStyle={styles.scrollContainer}
      showsVerticalScrollIndicator={true}
      persistentScrollbar={true} // Android only
      alwaysBounceVertical={true} // iOS
      nestedScrollEnabled={true}
      style={{
        ...Platform.select({
          web: {
            scrollbarColor: Color.primary + ' #ffffff', // thumb and track colors
          },
        }),
      }}
    />
  ) : (
    <View style={styles.container}>
      {isFetching ? (
        <ActivityIndicator
          size="large"
          style={[CommonStyles.flex1, CommonStyles.centerAlignment]}
          color={Color.primary}
        />
      ) : (
        <BaseText>No receipts found.</BaseText>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    backgroundColor: Color.accent,
  },
  itemContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    backgroundColor: Color.accent,
    borderBottomColor: Color.formDividerColor,
    borderBottomWidth: 1,
    ...CommonProperties.expenseFieldHeight,
  },
  imageContainer: {
    width: scale(75),
    height: scale(60),
    borderRadius: scale(5),
    overflow: 'hidden',
    marginRight: 10,
  },
  pdfIconContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    backgroundColor: Color.grayText2, // Background color for PDF icon
    borderRadius: scale(5),
  },
  pdfIconText: {
    color: Color.white,
    fontSize: 16,
    fontWeight: 'bold',
  },
  image: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  detailsContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  merchantText: {
    ...CommonProperties.h4,
    // fontWeight: 'bold',
    color: Color.black,
  },
  dateText: {
    ...CommonProperties.h4,
    color: Color.black,
  },
  amountText: {
    ...CommonProperties.h4,
    // fontWeight: 'bold',
    color: Color.black,
    marginRight: 10,
  },
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default PhotoUploads;
